// src/features/chat/models/hisTimelineModel.js
/*
 * Pure presentation model for the private HIS timeline.
 *
 * Merges chat messages with Zero-effort artifacts (future-action commitments,
 * effect receipts, approval requests) into one display list. This is a
 * display-only merge: artifacts remain a separate control-plane read model and
 * are never chat messages.
 *
 * Ordering contract (resolved design open contract):
 * - Commitments sort by createdAt; receipts sort by executedAt || createdAt;
 *   approvals sort by createdAt.
 * - updatedAt is never a sort key, so settling/resolution never moves items.
 * - Ties: message < commitment < receipt < approval, then id.
 * - While older messages remain unloaded (hasMoreMessages), artifacts older
 *   than the oldest loaded message stay hidden until pagination reveals them.
 */

const KIND_RANK = {
  message: 0,
  commitment: 1,
  receipt: 2,
  approval: 3,
};

/**
 * Coerce a timestamp-like value (Firestore Timestamp, Date, ISO string,
 * {seconds, nanoseconds}) to milliseconds.
 * @param {*} value
 * @returns {number} Milliseconds, or 0 when unknown.
 */
export function coerceTimestampMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value.seconds === 'number') {
    return (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1e6);
  }
  return 0;
}

/**
 * Stable materialization time for an artifact. Never uses updatedAt.
 * @param {Object} artifact
 * @param {'commitment'|'receipt'|'approval'} kind
 * @returns {number}
 */
export function artifactSortMs(artifact, kind) {
  if (kind === 'receipt') {
    return coerceTimestampMs(artifact?.executedAt || artifact?.createdAt);
  }
  return coerceTimestampMs(artifact?.createdAt);
}

function compareArtifactItems(a, b) {
  if (a.sortMs !== b.sortMs) return a.sortMs - b.sortMs;
  const rankDiff = KIND_RANK[a.kind] - KIND_RANK[b.kind];
  if (rankDiff !== 0) return rankDiff;
  return String(a.id).localeCompare(String(b.id));
}

/**
 * Merge messages and artifacts into one ordered display list.
 *
 * Messages keep their given order (they arrive sorted from the chat feed).
 * An artifact lands after every message whose tsMs <= its sortMs.
 *
 * @param {Object} params
 * @param {Array} params.messages - Normalized chat messages, ascending.
 * @param {Array} params.commitments - Future-action commitment docs.
 * @param {Array} params.receipts - Effect receipt docs.
 * @param {Array} params.approvals - Approval request docs.
 * @param {boolean} params.hasMoreMessages - Older messages remain unloaded.
 * @returns {Array} Timeline items.
 */
export function buildHisTimeline({
  messages = [],
  commitments = [],
  receipts = [],
  approvals = [],
  hasMoreMessages = false,
} = {}) {
  const oldestLoadedMs = messages.length ? (messages[0].tsMs || 0) : null;

  const artifactItems = [];
  const pushArtifact = (artifact, kind) => {
    if (!artifact || !artifact.id) return;
    const sortMs = artifactSortMs(artifact, kind);
    if (hasMoreMessages && (oldestLoadedMs === null || sortMs < oldestLoadedMs)) {
      // Hidden until pagination reaches its time range.
      return;
    }
    artifactItems.push({
      kind,
      id: `${kind}:${artifact.id}`,
      artifactId: artifact.id,
      sortMs,
      [kind]: artifact,
    });
  };
  commitments.forEach((commitment) => pushArtifact(commitment, 'commitment'));
  receipts.forEach((receipt) => pushArtifact(receipt, 'receipt'));
  approvals.forEach((approval) => pushArtifact(approval, 'approval'));
  artifactItems.sort(compareArtifactItems);

  const items = [];
  let artifactIndex = 0;
  for (const message of messages) {
    const messageTs = message.tsMs || 0;
    while (
      artifactIndex < artifactItems.length &&
      artifactItems[artifactIndex].sortMs < messageTs
    ) {
      items.push(artifactItems[artifactIndex]);
      artifactIndex += 1;
    }
    items.push({
      kind: 'message',
      id: message.id,
      sortMs: messageTs,
      message,
    });
  }
  while (artifactIndex < artifactItems.length) {
    items.push(artifactItems[artifactIndex]);
    artifactIndex += 1;
  }

  return items;
}

/**
 * Insert date divider items where the local calendar date changes.
 * Items with unknown timestamps (sortMs <= 0) never start a new date.
 * @param {Array} items - Timeline items from buildHisTimeline.
 * @returns {Array}
 */
export function insertDateDividers(items) {
  const result = [];
  let previousDateKey = null;
  for (const item of items) {
    if (item.sortMs > 0) {
      const date = new Date(item.sortMs);
      const dateKey = date.toDateString();
      if (dateKey !== previousDateKey) {
        result.push({
          kind: 'divider',
          id: `divider:${dateKey}`,
          sortMs: item.sortMs,
          dateMs: item.sortMs,
        });
        previousDateKey = dateKey;
      }
    }
    result.push(item);
  }
  return result;
}

/**
 * Collapse contiguous runs of >2 settled whispers into one fold item.
 *
 * The caller decides foldability (settled receipts and resolved approvals
 * fold; pending cards, fresh artifacts, and failure/conflict traces never
 * fold — they break the run). Folds keep a stable id from their first member.
 *
 * @param {Array} items - Timeline items (with dividers already inserted).
 * @param {Object} params
 * @param {(item: Object) => boolean} params.isFoldable
 * @param {number} [params.minRun=3]
 * @returns {Array}
 */
export function foldSettledRuns(items, { isFoldable, minRun = 3 } = {}) {
  if (typeof isFoldable !== 'function') return items;
  const result = [];
  let run = [];

  const flushRun = () => {
    if (run.length >= minRun) {
      result.push({
        kind: 'fold',
        id: `fold:${run[0].id}`,
        sortMs: run[0].sortMs,
        items: run,
        count: run.length,
      });
    } else {
      result.push(...run);
    }
    run = [];
  };

  for (const item of items) {
    const foldable =
      (item.kind === 'receipt' || item.kind === 'approval') && isFoldable(item);
    if (foldable) {
      run.push(item);
    } else {
      flushRun();
      result.push(item);
    }
  }
  flushRun();
  return result;
}

/**
 * The conversational frontier anchor: the latest assistant message, with no
 * newer user message after it. Artifacts, dividers, and folds are skipped.
 * @param {Array} items - Timeline items.
 * @returns {string|null} Message id, or null.
 */
export function latestAssistantAnchorId(items) {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (item.kind !== 'message') continue;
    return item.message.role === 'assistant' ? item.message.id : null;
  }
  return null;
}
